import React, { useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams, Link } from 'react-router-dom';
import { Row, Col } from 'reactstrap';
import { getOneRosterClasses } from '../../store/OneRoster/OneRosterClassAction';
import StatusCode from '../../util/StatusCode';
import debounce from 'lodash/debounce';
import * as Helper from '../../util/Helper';
import TableHorizontal from '../misc/TableHorizontal';
import * as OneRosterClassStatus from '../../util/OneRoster/OneRosterClassStatusUtil';
import Search from '../misc/Search';
import SelectInput from '../misc/SelectInput';
import FilterBottomComponent from '../misc/FilterBottomComponent';
import RequestParser from '../../util/RequestParser';
import { OneRosterClass } from '../../models/CoreOneRosterModels';
import { PageResult } from '../../models/CoreModels';
import { CacheRequestState } from '../../models/RequestState';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/store';


const OneRosterClasses = () => {
    const { t } = useTranslation(['common', 'oneRoster']);

    const dispatch = useDispatch();

    const [search, setSearch] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();

    const classes = useSelector<RootState, CacheRequestState<PageResult<OneRosterClass>>>(state => state.onerosterClass.classes);
    var filter = RequestParser.parseOneRosterClassSearchPageRequest(searchParams);

    useEffect(() => {
        if (filter.search) {
            setSearch(filter.search);
        }
    }, [dispatch]);

    useEffect(() => {
        getOneRosterClasses(filter, true)(dispatch);
        window.scrollTo(0, 0);
    }, [searchParams]);

    const updateFilter = (name: string, value: string | number | boolean | undefined) => {
        if (name !== 'page') {
            searchParams.set('page', '1');
        }

        if (value !== undefined && value !== '' && value !== null) {
            if (typeof (value) === 'string') {
                searchParams.set(name, value);
            } else {
                searchParams.set(name, value.toString());
            }
        } else {
            searchParams.delete(name);
        }

        setSearchParams(searchParams);
    }

    const debouncedSearch = useCallback(
        debounce(search => {
            updateFilter('search', search);
        }, 300), [searchParams]);

    const onSearch: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        e.preventDefault();
        let search = e.target.value;
        setSearch(search);
        debouncedSearch(search);
    }

    const clearFilter = () => {
        setSearch('');
        setSearchParams([]);
    }

    const mapClasses = () => {
        if (classes.code === StatusCode.COMPLETE || (classes.code === StatusCode.PENDING && classes.data !== undefined)) {
            if (classes.data === undefined) {
                return [];
            }
            return classes.data?.values.map(c => ({
                id: c.id,
                data: [
                    { value: <Link to={`/oneroster/class/${c.id}/information`}>{c.title}</Link> },
                    { value: t(`statusTypes.${c.status}`, { ns: 'oneRoster' }) },
                    { value: <Link to={`/oneroster/schoolunit/${c.schoolId}/information`} >{c.schoolName}</Link> },
                    { value: c.teacherCount },
                    { value: c.studentCount },
                    { value: Helper.formatDate(c.created), className: 'nowrap' },
                    { value: Helper.formatDate(c.modified), className: 'nowrap' },
                    { value: Helper.formatDate(c.inactivated), className: 'nowrap' },
                    { value: Helper.formatDate(c.archived), className: 'nowrap' },
                ]
            }));
        } else {
            return [];
        }
    }

    const renderClasses = () => {
        const classInfo = mapClasses();
        return (
            <TableHorizontal
                header={[{ value: t('genericTableHeaders.name', { ns: 'common' }) }, { value: t('commonTableHeaders.status', { ns: 'oneRoster' }) }, { value: t('commonTableHeaders.school', { ns: 'oneRoster' }) }, { value: t('commonTableHeaders.teachers', { ns: 'oneRoster' }) }, { value: t('commonTableHeaders.students', { ns: 'oneRoster' }) }, { value: t('genericTableHeaders.created', { ns: 'common' }) }, { value: t('genericTableHeaders.modified', { ns: 'common' }) }, { value: t('commonTableHeaders.inactivated', { ns: 'oneRoster' }) }, { value: t('commonTableHeaders.archived', { ns: 'oneRoster' }) }]}
                info={classInfo}
                error={classes.code === StatusCode.ERROR}
                searching={classes.code === StatusCode.PENDING}
                displayName={t('commonTableHeaders.sections', { ns: 'oneRoster' })}
                noResMsg={t('notFound.multiple', { ns: 'oneRoster', type: t('type.sections', { ns: 'oneRoster' }) })}
            />
        )
    }

    const renderFilter = () => {
        return (
            <Row>
                <Col sm="6">
                    <Search value={search} onChange={onSearch} placeholder={t('search', { ns: 'oneRoster', type: t('type.sections', { ns: 'oneRoster' }) })} />
                </Col>
                <Col sm="6">
                    <SelectInput
                        items={OneRosterClassStatus.StatusTypes.map(statusType => ({ value: statusType.id, text: `${t('filter.on', { ns: 'common' })} ${t(`statusTypes.${statusType.id}`, { ns: 'oneRoster' })}` }))}
                        onChange={(name, value) => updateFilter(name, value)}
                        name="status"
                        selected={filter.status}
                        defaultText={t('filter.status', { ns: 'common' })}
                    />
                </Col>
            </Row>
        )
    }

    return (
        <div className="container classes_oneroster">
            <div className="header--fixed">
                <h1>{t('object.oneRoster.section', { ns: 'common', count: 2 })}</h1>
                {renderFilter()}
            </div>
            <div className="body--scroll padding-40">
                {renderClasses()}
            </div>

            <FilterBottomComponent
                clearFilter={clearFilter}
                updateFilter={(value) => updateFilter('page', value)}
                requestState={classes}
            />

        </div>
    )
}

export default OneRosterClasses;
